import {
  createRouter,
  createWebHashHistory,
  RouteLocationNormalized,
  RouteRecordRaw,
} from 'vue-router'

import useTracking from '@/composables/useTracking'
import useCdcProps from '@/composables/useCdcProps'
import useProductScope from '@/composables/useProductScope'
import { PopupKey } from '@/composables/usePopUp'

import { LayoutBackroundColor } from '@/types/layout.interfaces'
import { Product } from '@/types/products.interfaces'

// MAIN VIEWS
const NavBar = () => import('@/views/NavBarView.vue')
const Home = () => import('@/views/home/HomeView.vue')
const ModulesCatalog = () => import('@/views/ModulesCatalogView.vue')
const Module = () => import('@/views/ModuleView.vue')
const ModuleModal = () => import('@/views/ModuleModalView.vue')
const ModuleMedias = () => import('@/views/ModuleMedias.vue')
const Recommendations = () => import('@/views/RecommendationsView.vue')
const CarriersBanner = () => import('@/views/CarriersBannerView.vue')
const RecommendationsCarrierView = () => import('@/views/RecommendationsCarrierView.vue')
const ModulesManagerMessage = () =>
  import('@/views/ModulesManagerMessageView.vue')

// DASHBOARD VIEWS
const DashboardMoreModule = () => import('@/views/dashboard/MoreModuleView.vue')
const DashboardTipsAndUpdate = () =>
  import('@/views/dashboard/TipsAndUpdateView.vue')
const DashboardNews = () => import('@/views/dashboard/NewsView.vue')
const LearnAndGrowYourBusiness = () =>
  import('@/views/dashboard/LearnAndGrowYourBusinessView.vue')
const PrestashopUpdate = () =>
  import('@/views/dashboard/PrestashopUpdateView.vue')
const ExploreThemeCatalog = () => import('@/views/ExploreThemeCatalogView.vue')

// DEPENDENCY INSTALLER VIEW
const DependencyResolver = () => import('@/views/DependencyResolverView.vue')

// DIALOG VIEWS
const CompatibilityControlDialog = () => import('@/views/dialog/CompatibilityControlDialog.vue')
const ComplianceFeatureTourDialog = () => import('@/views/dialog/ComplianceFeaturesTourDialogue.vue')
const VerifiedPlusDialog = () => import('@/views/dialog/VerifiedPlusDialog.vue')
const CarriersShippingCountryDialog = () => import('@/views/dialog/CarriersShippingCountryDialog.vue')

function moduleSearchQueryToProps(route: RouteLocationNormalized) {
  return {
    categoryId: route.query.categoryId,
    developerId: route.query.developerId,
    keyword: route.query.keyword,
    page: route.query.page ? parseInt(route.query.page as string) : undefined,
    rateAbove: route.query.rateAbove
      ? parseInt(route.query.rateAbove as string)
      : undefined,
    sortBy: route.query.sortBy,
    filters: route.query.filters,
    isVerifiedPlus: route.query.isVerifiedPlus === 'true' ? 'true' : undefined,
  }
}
const { xprops } = useCdcProps()
const { getScopeLabel, getScopeBackgroundColor } = useProductScope()

const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      components: { default: Home, navbar: NavBar },
      meta: {
        title: 'Home Page MBO',
      },
      props: {
        navbar: {
          theme: LayoutBackroundColor.dark,
          hideBreadcrumb: true,
        },
      },
    },
    {
      path: '/modules',
      name: 'modules',
      components: { default: ModulesCatalog, navbar: NavBar },
      meta: {
        title: 'Module Catalog',
        breadcrumb(route: RouteLocationNormalized) {
          return {
            label: (t: (s: string) => string) => t('modules_view.page_title'),
            link: route.path,
          }
        },
      },
      props: (route: RouteLocationNormalized) => ({
        ...moduleSearchQueryToProps(route),
        theme: LayoutBackroundColor.gray,
      }),
    },
    {
      path: '/modules/:scope_key',
      name: 'modules-scope',
      components: { default: ModulesCatalog, navbar: NavBar },
      props: (route: RouteLocationNormalized) => ({
        scopeKey: route.params.scope_key,
        theme: getScopeBackgroundColor(
          route.params.scope_key as string,
        ) as LayoutBackroundColor,
        ...moduleSearchQueryToProps(route),
      }),
      meta: {
        title: 'Module Catalog',
        breadcrumb(route: RouteLocationNormalized) {
          return {
            label: (t: (s: string) => string) =>
              getScopeLabel(route.params.scope_key as string, t),
            link: route.path,
          }
        },
      },
    },
    {
      name: 'module-modal',
      path: '/module/:id',
      props: (route) => {
        const id = route.params.id
        const {
          name,
          categoryId,
          categoryName,
          categoryParentEnglishName,
          categoryParentId,
          categoryParentName,
          purchased,
          scope,
        } = route.query
        return {
          id,
          name,
          categoryId,
          categoryName,
          categoryParentEnglishName,
          categoryParentId,
          categoryParentName,
          purchased: purchased === 'true' ? true : false,
          scope,
        }
      },
      component: ModuleModal,
      meta: {
        title: 'Module Modale',
      },
    },
    {
      name: 'module',
      path: '/module/:id/fullpage',
      props: (route: RouteLocationNormalized) => ({
        id: route.params.id,
        scope: route.query.scope,
        theme: LayoutBackroundColor.gray,
        hideUploadButton: true,
        data: route.meta.data,
        reviews: route.meta.reviews,
        algoliaQueryId: route.query.algolia_query_id,
        algoliaIndex: route.query.algolia_index,
      }),
      components: { default: Module, navbar: NavBar },
      meta: {
        title: 'Module',
        breadcrumb(route: RouteLocationNormalized) {
          const product = route.meta.data as Product
          return [
            {
              label: () => product.categoryParent?.name,
              link: router.resolve({
                name: 'modules',
                query: { categoryId: product.categoryParent?.id_category },
              }).fullPath,
            },
            {
              label: () => product.category?.name,
              link: router.resolve({
                name: 'modules',
                query: { categoryId: product.category?.id_category },
              }).fullPath,
            },
            {
              label: () => product.product_lang.product_name,
              link: route.path,
            },
          ]
        },
      },
    },
    {
      path: '/module/:id/medias',
      props: (route) => {
        const { id, name } = route.params
        const {
          categoryId,
          categoryName,
          categoryParentEnglishName,
          categoryParentId,
          categoryParentName,
          slide,
        } = route.query
        const currentSlide = parseInt((slide as string) || '0')
        return {
          id,
          name,
          currentSlide,
          categoryId,
          categoryName,
          categoryParentEnglishName,
          categoryParentId,
          categoryParentName,
        }
      },
      name: 'module-medias',
      component: ModuleMedias,
      meta: {
        title: 'ModuleZoom',
      },
    },
    {
      path: '/carriers-banner',
      name: 'carriers-banner',
      component: CarriersBanner,
      meta: {
        title: 'Carriers Banner',
      },
    },
    {
      path: '/recommendations',
      name: 'recommendations',
      component: Recommendations,
      meta: {
        title: 'Recommendations Pop-up',
        trackingProperties: {
          modules_scope: xprops?.controllerName || null,
        }
      },
    },
    {
      path: '/recommendations-carrier',
      name: 'recommendations-carrier',
      component: RecommendationsCarrierView ,
      meta: {
        title: 'Recommendations Carrier',
      },
    },
    {
      path: '/explore-theme-catalog',
      name: 'explore-theme-catalog',
      component: ExploreThemeCatalog,
      meta: {
        title: 'Theme & Logo',
      },
    },
    {
      path: '/modules-manager-message',
      name: 'modules-manager-message',
      component: ModulesManagerMessage,
      meta: {
        title: 'Warning Message Business Care',
      },
    },

    // Dashboard
    {
      path: '/dashboard/more-modules',
      name: 'dashboard-more-modules',
      component: DashboardMoreModule,
      meta: {
        title: 'Dashboard Add More Modules Block',
        skipTracking: true,
        skipPurchasedProduct: true,
        skipFetchPsAccount: true,
      },
    },
    {
      path: '/dashboard/tips-and-updates',
      name: 'dashboard-tips-and-updates',
      component: DashboardTipsAndUpdate,
      meta: {
        title: 'Dashboard Tips And Updates Block',
      },
    },
    {
      path: '/dashboard/news',
      name: 'dashboard-news',
      component: DashboardNews,
      meta: {
        title: 'Dashboard Prestashop News Block',
        skipTracking: true,
        skipPurchasedProduct: true,
        skipFetchPsAccount: true,
      },
    },
    {
      path: '/dashboard/ps-update',
      name: 'dashboard-ps-update',
      component: PrestashopUpdate,
      meta: {
        title: 'Dashboard Prestashop Updates Block',
        skipTracking: true,
        skipPurchasedProduct: true,
        skipFetchPsAccount: true,
      },
    },
    {
      path: '/dashboard/learn-and-grow-your-business',
      name: 'learn-and-grow-your-business',
      component: LearnAndGrowYourBusiness,
      meta: {
        title: 'Dashboard Learn & Grow your business',
        skipTracking: true,
        skipPurchasedProduct: true,
        skipFetchPsAccount: true,
      },
    },

    // Dependency installer
    {
      path: '/dependencies-resolver',
      name: 'dependencies-resolver',
      component: DependencyResolver,
      meta: {
        title: 'Dependencies Resolver',
        provider: 'resolver',
      },
    },

    // Dialog routes
    {
      path: '/dialog/compatibility-control',
      name: 'compatibility-control-dialog',
      component: CompatibilityControlDialog,
      meta: {
        title: 'Compatibility Control Dialog',
        provider: 'dialog',
      },
    },

        {
      path: `/dialog/verified-plus`,
      name: `verified-plus`,
      component: VerifiedPlusDialog,
      meta: {
        title: 'Verified Plus Dialog',
        provider: 'dialog',
      },
    },

    {
      path: `/dialog/${PopupKey.COMPLIANCE_FEATURES_TOUR}`,
      name: `${PopupKey.COMPLIANCE_FEATURES_TOUR}-dialog`,
      component: ComplianceFeatureTourDialog,
      meta: {
        title: 'Compliance Features Tour Dialog',
        provider: 'dialog',
      },
    },

    {
        path: '/dialog/carriers-shipping-modal',
        name: 'carriers-shipping-modal',
        component: CarriersShippingCountryDialog,
        meta: {
            title: 'Carriers Shipping Modal',
            provider: 'dialog',
            trackingProperties: {
              modules_scope: xprops?.controllerName || null,
              clicked_from: xprops?.dialogContext?.clicked_from || '',
            }
        },
    },
  ] as RouteRecordRaw[],
})


router.afterEach(async (to, from) => {
  document.title = `MBO CDC - ${to.meta.title}`
  if(!to.meta.skipTracking) {
    const { page } = useTracking()
    page(to.meta.title as string, to.meta.trackingProperties as any)
  }

  if (
    ['home', 'modules', 'modules-scope', 'module'].includes(
      to.name as string,
    ) &&
    from.fullPath !== to.fullPath
  ) {
    const handleCdcNavigate = (window as any).xprops.handleCdcNavigate
    handleCdcNavigate('/#' + to.path)
  }
})

export default router
